import { Handshake } from '@lucide/vue'

import { AssetWizardFlow } from '@/store/ai/assetWizard/utils/types'

import {
  amountField,
  assetNameField,
  currencyField,
  dateField,
  descriptionField,
} from '../utils/fields'
import {
  SUMMARY_STEP,
  TAGS_STEP,
  valueStep,
} from '../utils/steps'
import { CASH_FLOWS_STEP_KEY, INVESTORS_STEP_KEY } from '../utils/const'
import { WizardDefinition } from '../utils/types'
import { TagRef } from '@types'

import { privateFundSubscribeAndCommitSchema } from './schema'
import { create } from './create'
import CashFlowsStep from '../components/CashFlowsStep.vue'
import InvestorsStep from '../components/InvestorsStep.vue'

/** Same string the legacy Private fund wizard files commitments under. */
const FUND_TYPE = 'private fund'

const DEFAULT_TAGS: TagRef[] = [
  { tag_name: 'Asset Class', tag_value: 'Alternative Investments' },
  { tag_name: 'Investment Strategy', tag_value: 'Private Equity' },
]

/**
 * Private equity, private credit, venture and the rest, split across more
 * than one investing entity — see PrivateFund for the single-owner sibling
 * this one used to be the only shape of, before that split existed.
 */
export const privateFundSubscribeAndCommitWizard: WizardDefinition = {
  flow: AssetWizardFlow.PRIVATE_FUND_SUBSCRIBE_AND_COMMIT,
  label: 'Private fund · subscribe and commit',
  icon: Handshake,
  schema: privateFundSubscribeAndCommitSchema,
  steps: [
    {
      key: 'fund',
      label: 'Fund',
      heading: 'What is this?',
      description:
        'Private equity, private credit, venture and hedge funds all go here.',
      fields: [
        assetNameField('Fund name', 'Name this fund'),
        descriptionField(),
      ],
    },
    {
      key: 'commitment',
      label: 'Commitment',
      heading: 'How much did you commit?',
      description:
        'The amount pledged to this fund — not what has been called yet. Calls and distributions have their own step.',
      fields: [
        amountField('Committed amount'),
        dateField('Date of investment'),
        currencyField(),
      ],
    },
    /*
     * Reachable only once the commitment above it is known — a split has
     * nothing to divide until there is a number to divide it against.
     */
    {
      key: INVESTORS_STEP_KEY,
      label: 'Investing entities',
      heading: 'Who is investing?',
      description: 'Each investor becomes its own asset and transaction bundle in the fund.',
      component: InvestorsStep,
    },
    {
      key: CASH_FLOWS_STEP_KEY,
      label: 'Cash flows',
      heading: 'Any capital calls or distributions yet?',
      badge: 'Optional',
      description: 'Capital calls, fees and distributions to date. Add them now or log them as they arrive.',
      component: CashFlowsStep,
    },
    /*
     * The shared valuation step, in a fund's words: what a fund is worth is a
     * NAV struck on a date, which is the same mark every other flow records.
     */
    valueStep({
      heading: 'What is the latest NAV?',
      description:
        'Record the NAV and the date it was struck. Each mark dates the value it sets.',
    }),
    TAGS_STEP,
    SUMMARY_STEP,
  ],
  records: [
    {
      key: 'fund',
      label: 'Fund',
      icon: Handshake,
      tone: 'brand',
      transactionType: 'subscribe and commit',
      heading: 'assetName',
      fields: ['amount', 'date', 'currency', 'description'],
    },
  ],
  constants: { assetType: FUND_TYPE },
  defaultTags: DEFAULT_TAGS,
  create,
}
